/**
 * Parse a date string to DD/MM/YYYY format
 * @param {string} dateString - The date string to parse
 * @returns {string} Formatted date string
 */
export const parseDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Whether a nota spesa is an income entry rather than an expense.
 * @param {Object} nota - The expense/income note
 * @returns {boolean}
 */
export const isEntrata = (nota) => nota?.tipo === "entrata";

/**
 * A nota spesa's amount, signed so income offsets expenses when summed
 * (positive for a spesa, negative for an entrata).
 * @param {Object} nota - The expense/income note
 * @returns {number}
 */
export const getSignedImporto = (nota) => {
  const importo = nota?.importo || 0;
  return isEntrata(nota) ? -importo : importo;
};

/**
 * Calculate the net total (expenses minus income) from an array of nota spese
 * @param {Array} notaSpese - Array of expense/income notes
 * @returns {string} Total amount formatted to 2 decimal places
 */
export const getTotale = (notaSpese) => {
  if (!notaSpese || notaSpese.length === 0) return "0.00";

  const importoSummed = notaSpese.reduce(
    (accumulator, spesa) => accumulator + getSignedImporto(spesa),
    0
  );

  return importoSummed?.toFixed(2);
};

/**
 * Format a date as a short relative time ("2h ago", "Yesterday") when recent,
 * falling back to a compact DD/MM date otherwise.
 * @param {string} dateString - The date string to format
 * @returns {string} Relative or short date string
 */
export const formatRelativeDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Whether the email is valid
 */
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Get category name from categories array
 * @param {Array} categories - Array of category objects
 * @param {string} categoryId - Category ID to find
 * @returns {string} Category name or "Uncategorized"
 */
export const getCategoryName = (categories, categoryId) => {
  if (!categoryId || !categories) return "Uncategorized";
  const category = categories.find((cat) => cat._id === categoryId);
  return category?.name || "Uncategorized";
};
